// Post-login account setup actions (non-blocking modal)
// See docs/design/account-setup-actions.md

angular.module('dentalLinks')
  .factory('AccountSetupActions', ['$q', '$transitions', '$uiRouterGlobals', '$uibModal', 'ModalHandler', 'Auth', 'Practice', 'User', 'Role', 'USER_ROLES', 'Logger',
    function ($q, $transitions, $uiRouterGlobals, $uibModal, ModalHandler, Auth, Practice, User, Role, USER_ROLES, Logger) {
      // The ui-router states the dialog may open on: the signed-in work pages.
      // A name also covers its child states ('admin' covers 'admin.users').
      // Everything else is left out on purpose, including error_page, the auth
      // and email-link pages, and the super-user console.
      var ALLOWED_STATES = ['history', 'viewReferral', 'createReferral', 'reviewReferral', 'activity', 'admin'];

      var dismissedThisSession = false;
      var modalOpen = false;
      // Set when a check is skipped because of the page it was made on. The
      // check is repeated on the next allowed page instead of being dropped.
      var deferred = false;
      // The last transition a deferred check waited on, so a check deferred
      // again during the same transition does not wait on it twice.
      var awaited = null;

      function isAllowedState(state) {
        var name = state && state.name;
        if (!name) {
          return false;
        }
        return ALLOWED_STATES.some(function (allowed) {
          return name === allowed || name.indexOf(allowed + '.') === 0;
        });
      }

      // A check made during a transition (the post-login redirect, a guard's
      // redirect to error_page) is judged by where that transition lands, not
      // by the page being left, so it never passes while one is running.
      function onAllowedPage() {
        return !$uiRouterGlobals.transition && isAllowedState($uiRouterGlobals.current);
      }

      function isAuthenticated() {
        var auth = Auth.get();
        return !!(auth && auth.id && auth.practice_id);
      }

      function isAdminFromAuthContext() {
        var auth = Auth.get();
        if (!auth) {
          return false;
        }
        var roles = Role.getRolesByNames(auth.roles || []);
        return Role.hasRoles([USER_ROLES.admin], roles);
      }

      function loadPracticeWithUsers(practiceId) {
        return Practice.get({ practiceId: practiceId }).$promise.then(function (practice) {
          if (practice && Array.isArray(practice.users)) {
            return practice;
          }
          // Fallback: some practice payloads may omit users; load them separately.
          return User.getAllUsers({ practice_id: practiceId }).$promise.then(function (users) {
            practice = practice || {};
            practice.users = users || [];
            return practice;
          });
        });
      }

      function doctorUsers(practice) {
        var users = (practice && practice.users) || [];
        return users.filter(function (u) {
          if (!u || typeof u.roles_mask === 'undefined' || u.roles_mask === null) {
            return false;
          }
          return Role.hasRoles([USER_ROLES.doctor], Role.getFromMask(u.roles_mask));
        });
      }

      function hasMissingDoctorSpecialties(practice) {
        return doctorUsers(practice).some(function (u) {
          return !u.specialty_type_id;
        });
      }

      function shouldShowDoctorSpecialtiesAction(practice) {
        if (!isAdminFromAuthContext()) {
          return false;
        }
        if (!practice || !practice.practice_type || practice.practice_type.code !== 'multi_specialty') {
          return false;
        }
        return hasMissingDoctorSpecialties(practice);
      }

      // Re-runs the deferred check once the transition settles, whatever the
      // outcome: on success the user is on its destination, on rejection they
      // are still on the page it started from, and on replacement the check
      // finds the newer transition running and waits on that one instead.
      function rerunAfter(transition) {
        if (awaited === transition) {
          return;
        }
        awaited = transition;
        transition.promise.then(rerun, rerun);
      }

      function rerun() {
        if (deferred) {
          deferred = false;
          evaluate();
        }
      }

      // With no transition running, the onStart hook below picks up the next one.
      function defer() {
        deferred = true;
        if ($uiRouterGlobals.transition) {
          rerunAfter($uiRouterGlobals.transition);
        }
        return false;
      }

      function evaluate() {
        if (!isAuthenticated() || dismissedThisSession || modalOpen) {
          return $q.resolve(false);
        }

        var auth = Auth.get();
        if (!auth || !auth.practice_id) {
          return $q.resolve(false);
        }

        if (!onAllowedPage()) {
          return $q.resolve(defer());
        }

        return loadPracticeWithUsers(auth.practice_id).then(function (practice) {
          if (!shouldShowDoctorSpecialtiesAction(practice)) {
            return false;
          }
          // Another check (the nav's and the login's can overlap) may have
          // opened the dialog while the practice was loading, and the user
          // may already have dismissed it with "Remind me later".
          if (modalOpen || dismissedThisSession) {
            return false;
          }
          // The user may have moved on to a disallowed page meanwhile.
          if (!onAllowedPage()) {
            return defer();
          }

          modalOpen = true;
          var modalInstance = $uibModal.open({
            templateUrl: 'partials/account_setup_actions/setup_actions_dialog.html',
            controller: 'AccountSetupActionsModalController',
            resolve: {
              practice: function () { return practice; }
            }
          });
          ModalHandler.set(modalInstance);

          function closed() {
            modalOpen = false;
          }
          modalInstance.result.then(closed, closed);

          return true;
        }).catch(function (e) {
          // Never block login or navigation if setup actions fail to load.
          Logger.log('Account setup actions: failed to evaluate', {
            status: e && e.status,
            statusText: e && e.statusText
          });
          return false;
        });
      }

      // A check deferred on a page the user has settled on waits for the next one.
      $transitions.onStart({}, function (transition) {
        if (deferred) {
          rerunAfter(transition);
        }
      });

      // A navigation closes the dialog: app.js's $stateChangeStart handler,
      // which the stateEvents polyfill runs from a priority-1000 onBefore
      // hook, dismisses any open modal. This hook runs ahead of it, so the
      // dialog is owed again however the navigation ends: on the page it lands
      // on, or on the page the user stays on when a later onBefore hook aborts
      // it (a guard's preventDefault, the unsaved-changes prompt). Such a
      // transition never becomes $uiRouterGlobals.transition and never fires
      // onStart, so this is the only hook that sees it. When nothing ends up
      // dismissing the dialog, the re-run finds it still open and does nothing.
      $transitions.onBefore({}, function (transition) {
        if (modalOpen && !dismissedThisSession) {
          deferred = true;
          rerunAfter(transition);
        }
      }, { priority: 1001 });

      return {
        checkAndShow: evaluate,
        dismissForSession: function () { dismissedThisSession = true; },
        // For a dialog button that closes the dialog and then navigates: the
        // dialog is not reopened after that navigation, but a later check may
        // still show it.
        closeForNavigation: function () { modalOpen = false; },
        reset: function () {
          dismissedThisSession = false;
          modalOpen = false;
          deferred = false;
        }
      };
    }])

  .controller('AccountSetupActionsModalController', ['$scope', '$uibModalInstance', '$state', 'ModalHandler', 'AccountSetupActions', 'practice',
    function ($scope, $uibModalInstance, $state, ModalHandler, AccountSetupActions, practice) {
      // Practice data shared with all action controllers via scope inheritance.
      $scope.practice = practice;

      // Shared state for all actions.
      $scope.submitted = false;

      // Registry of action validators. Each action controller registers its validator here.
      // Key: action key (string), Value: function that returns true if valid.
      var actionValidators = {};

      // Called by child action controllers to register their validation function.
      // The validator should return true if the action is valid (or not applicable).
      $scope.registerActionValidator = function (actionKey, validatorFn) {
        actionValidators[actionKey] = validatorFn;
      };

      // Check if all registered action validators pass.
      function allActionsValid() {
        for (var key in actionValidators) {
          if (actionValidators.hasOwnProperty(key)) {
            if (!actionValidators[key]()) {
              return false;
            }
          }
        }
        return true;
      }

      $scope.remindLater = function () {
        AccountSetupActions.dismissForSession();
        ModalHandler.dismiss($uibModalInstance);
      };

      $scope.done = function () {
        $scope.submitted = true;
        if (!allActionsValid()) {
          // Validation failed; don't close the dialog.
          return;
        }
        ModalHandler.close($uibModalInstance);
      };

      $scope.goToAccountSettings = function () {
        // Let user jump to the canonical settings screen in a new state. The
        // user took the setup there, so the dialog is not reopened on top of
        // it, but unlike "Remind me later" this does not dismiss it for the
        // session.
        AccountSetupActions.closeForNavigation();
        ModalHandler.dismiss($uibModalInstance);
        $state.go('admin.practice');
      };
    }])

  .controller('DoctorSpecialtiesForMultiSpecialtySetupActionController', ['$scope', 'Procedure', 'Practice', 'User', 'Logger', 'Notification',
    function ($scope, Procedure, Practice, User, Logger, Notification) {
      // This controller expects `practice` on scope (from parent modal controller via resolve).
      var ACTION_KEY = 'doctor_specialties_for_multi_specialty';

      $scope.practiceTypes = [];
      $scope.specialtyOptions = [];
      $scope.loadingPracticeSave = false;
      $scope.practiceSaveError = null;
      $scope.savingUserIds = {};
      $scope.userSaveErrors = {};

      // Load canonical list of practice types from backend.
      Procedure.practiceTypes({ include_procedures: false },
        function (types) {
          // Normalize ids so ng-options matches model values reliably.
          (types || []).forEach(function (t) {
            if (t && typeof t.id !== 'undefined' && t.id !== null) {
              t.id = Number(t.id);
            }
          });
          $scope.practiceTypes = types || [];
          $scope.specialtyOptions = (types || []).filter(function (t) {
            return t && t.code !== 'multi_specialty';
          });
        },
        function (failure) {
          Logger.error('Failed to load practice types for account setup actions', failure);
        }
      );

      function doctorUsers() {
        return ($scope.practice.users || []).filter(function (u) {
          return u && (u.roles_mask & 2); // doctor mask
        }).map(function (u) {
          // Normalize `specialty_type_id` so ng-options matches even if API returns strings.
          if (u && typeof u.specialty_type_id !== 'undefined' && u.specialty_type_id !== null && u.specialty_type_id !== '') {
            u.specialty_type_id = Number(u.specialty_type_id);
          }
          return u;
        });
      }

      $scope.doctors = doctorUsers();

      $scope.$watch('practice.users', function () {
        $scope.doctors = doctorUsers();
      }, true);

      $scope.showDoctorList = function () {
        return $scope.practice &&
          $scope.practice.practice_type &&
          $scope.practice.practice_type.code === 'multi_specialty';
      };

      // Check if a specific doctor is missing a specialty (for validation display).
      $scope.isDoctorMissingSpecialty = function (doctor) {
        return doctor && !doctor.specialty_type_id;
      };

      // Validation: returns true if valid (or if validation doesn't apply).
      function isValid() {
        // Only validate when practice is multi-specialty.
        if (!$scope.showDoctorList()) {
          return true;
        }
        // Check that all doctors have a specialty assigned.
        return !$scope.doctors.some(function (doctor) {
          return !doctor.specialty_type_id;
        });
      }

      // Register this action's validator with the parent modal controller.
      if ($scope.registerActionValidator) {
        $scope.registerActionValidator(ACTION_KEY, isValid);
      }

      function updatePracticeOnServer() {
        $scope.loadingPracticeSave = true;
        $scope.practiceSaveError = null;
        var existingUsers = $scope.practice && $scope.practice.users;
        var existingPracticeType = $scope.practice && $scope.practice.practice_type;

        return Practice.update({ practiceId: $scope.practice.id }, {
          practice: {
            name: $scope.practice.name,
            practice_type_id: $scope.practice.practice_type && $scope.practice.practice_type.id,
            addresses_attributes: $scope.practice.addresses || []
          }
        }).$promise.then(function (updated) {
          // Keep local model aligned to server response.
          $scope.practice = updated;
          if ($scope.practice && !Array.isArray($scope.practice.users) && Array.isArray(existingUsers)) {
            $scope.practice.users = existingUsers;
          }
          // Some practice payloads omit `practice_type` (or omit `code`). Keep the selected type object
          // so the UI can toggle multi-specialty back and forth without losing state.
          if (existingPracticeType && $scope.practice) {
            var missingPracticeType = !$scope.practice.practice_type || !$scope.practice.practice_type.code;
            if (missingPracticeType) {
              $scope.practice.practice_type = existingPracticeType;
            }
            if (!$scope.practice.practice_type_id && existingPracticeType.id) {
              $scope.practice.practice_type_id = existingPracticeType.id;
            }
          }
          $scope.loadingPracticeSave = false;
          return updated;
        }).catch(function (failure) {
          $scope.loadingPracticeSave = false;
          $scope.practiceSaveError = 'Failed to save practice type';
          Notification.error('An error occurred during account update...');
          throw failure;
        });
      }

      $scope.onPracticeTypeChanged = function () {
        // Do not auto-close; user can switch back and forth and click Done when finished.
        return updatePracticeOnServer();
      };

      $scope.saveDoctorSpecialty = function (doctor) {
        if (!doctor || !doctor.id) {
          return;
        }
        $scope.savingUserIds[doctor.id] = true;
        $scope.userSaveErrors[doctor.id] = null;

        User.updateSpecialty({ id: doctor.id }, { specialty_type_id: doctor.specialty_type_id }).$promise
          .then(function () {
            $scope.savingUserIds[doctor.id] = false;
          })
          .catch(function (failure) {
            $scope.savingUserIds[doctor.id] = false;
            $scope.userSaveErrors[doctor.id] = 'Failed to save specialty';
            Notification.error('An error occurred while saving specialty');
            Logger.error('Failed to update doctor specialty', failure);
          });
      };
    }]);

